"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { MapPin, Building2, Star, Code as Code2, FileText, Search, LayoutGrid, Link2, Globe, ChartBar as BarChart3, Smartphone, TrendingUp, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
const localServices = [{
  id: "gbp-optimization",
  title: "Google Business Profile Optimization",
  badge: "Core Signal",
  description: "I personally optimize your GBP categories, geo-tagged assets, attributes, and weekly updates to dominate the local 3-pack.",
  icon: MapPin,
  highlight: "Primary Local Pack Driver"
}, {
  id: "local-citations",
  title: "Local Citation Building & Audits",
  badge: "Authority",
  description: "I build and audit clean NAP (Name, Address, Phone) consistency across premier UK business directories to build bulletproof local trust.",
  icon: Building2,
  highlight: "100% Manual Directory Submissions"
}, {
  id: "review-management",
  title: "Review Strategy & Reputation",
  badge: "Conversion",
  description: "I implement custom automated review collection systems that generate authentic customer ratings and improve local search velocity.",
  icon: Star,
  highlight: "Automated Feedback Loops"
}, {
  id: "local-schema",
  title: "Local Schema & Structured Data",
  badge: "Technical",
  description: "I craft bespoke JSON-LD LocalBusiness schema code declaring service boundaries, opening hours, and geo-coordinates to search engines.",
  icon: Code2,
  highlight: "Custom Machine-Readable Code"
}, {
  id: "geo-content",
  title: "Geo-Targeted Content Strategy",
  badge: "Content",
  description: "I write high-converting location pages and localized landing experiences tailored to the specific search intent of your target towns.",
  icon: FileText,
  highlight: "Hyper-Local Landing Pages"
}, {
  id: "keyword-research",
  title: "Local Keyword Research & Intent",
  badge: "Strategy",
  description: "I uncover high-value search queries with local intent and 'near me' modifiers that your local competitors are ignoring.",
  icon: Search,
  highlight: "High-Intent Buyer Terms"
}, {
  id: "onpage-signals",
  title: "On-Page Local SEO Signals",
  badge: "Optimization",
  description: "I optimize meta titles, H1-H3 headers, embedded maps, localized internal links, and localized footer signals across your site.",
  icon: LayoutGrid,
  highlight: "Complete On-Page Alignment"
}, {
  id: "local-backlinks",
  title: "Local Backlink Acquisition",
  badge: "Authority",
  description: "I earn contextual backlinks from regional news platforms, local business associations, and relevant industry partners.",
  icon: Link2,
  highlight: "Genuine Regional Coverage"
}, {
  id: "multi-location",
  title: "Multi-Location SEO Management",
  badge: "Scale",
  description: "I engineer scalable search frameworks for multi-branch regional brands to maximize coverage without keyword cannibalization.",
  icon: Globe,
  highlight: "Scalable Multi-Branch Growth"
}, {
  id: "competitor-analysis",
  title: "Local Competitor Intelligence",
  badge: "Analysis",
  description: "I reverse-engineer top-ranking map pack competitors to uncover their exact citation, link, and content advantage.",
  icon: BarChart3,
  highlight: "Actionable Gap Audits"
}, {
  id: "mobile-voice",
  title: "Mobile & Voice Search SEO",
  badge: "Technical",
  description: "I ensure lightning-fast mobile performance and optimize content for conversational voice queries on Siri, Alexa, and Google Assistant.",
  icon: Smartphone,
  highlight: "Mobile-First Infrastructure"
}, {
  id: "tracking-reporting",
  title: "Geo-Grid Rank & Call Tracking",
  badge: "Reporting",
  description: "I provide transparent geo-grid ranking maps and call tracking analytics so you see exact lead attribution down to the mile.",
  icon: TrendingUp,
  highlight: "Transparent Grid Metrics"
}];
export default function Services() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.06
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="localseoservicesgrid" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="absolute top-0 right-0 -z-10 size-96 rounded-full bg-secondary/30 blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -z-10 size-96 rounded-full bg-accent/10 blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center mb-16 space-y-4">
          <Badge variant="secondary" className="px-3.5 py-1 text-sm font-medium tracking-wide">
            Hands-On Local Search Expertise
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight font-default text-foreground">
            Bespoke Local SEO Services Designed for High-Intent Growth
          </h2>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            You work directly with me — Yassine Essaadi. I implement every component of your local SEO campaign with precision to capture local search market share.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {localServices.map((service, i) => {
          const IconComponent = service.icon;
          return <motion.div key={service.id} variants={cardVariants} data-index={i}>
                <Card className="h-full flex flex-col justify-between border border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] duration-300 hover:border-primary/40 overflow-hidden group">
                  <div>
                    <div className="bg-primary text-primary-foreground p-5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-md bg-primary-foreground/10 text-primary-foreground">
                          <IconComponent className="size-5" />
                        </div>
                        <Badge variant="secondary" className="bg-primary-foreground/20 text-primary-foreground border-transparent">
                          {service.badge}
                        </Badge>
                      </div>
                    </div>

                    <CardHeader className="pt-6 pb-2">
                      <CardTitle className="text-xl font-semibold text-card-foreground group-hover:text-primary transition-colors duration-200">
                        {service.title}
                      </CardTitle>
                    </CardHeader>

                    <CardContent className="space-y-4 text-card-foreground">
                      <CardDescription className="text-muted-foreground leading-relaxed text-sm">
                        {service.description}
                      </CardDescription>

                      <div className="flex items-center gap-2 pt-2 text-xs font-medium text-foreground/80">
                        <CheckCircle2 className="size-4 text-accent shrink-0" />
                        <span>{service.highlight}</span>
                      </div>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-2">
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between text-primary hover:text-primary hover:bg-muted font-medium transition-colors group/btn">
                      <Link to="/ContactPage">
                        <span>Consult on {service.badge}</span>
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover/btn:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        <div className="mt-16 bg-muted/50 rounded-2xl p-8 sm:p-10 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="text-2xl font-bold font-default text-foreground">
              Ready to dominate your target local markets?
            </h3>
            <p className="text-muted-foreground text-sm sm:text-base max-w-xl">
              Book a direct consultation with Yassine to evaluate your local map pack positioning and audit your competitors.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto shrink-0">
            <Button asChild size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/ContactPage">Book a Free SEO Consultation</Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto font-medium">
              <Link to="/CaseStudiesPage">View My Case Studies</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}